import {
  Button,
  Checkbox,
  Icon,
  Modal,
  Toast,
  Tooltip,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  contacts,
  companies,
  offices,
  contactRoles,
  externalReferences,
  type Company,
  type Contact,
  type ContactRole,
  type ExternalReference,
  type Office,
  type ContactTranslation,
} from "../../../api/addressBook.service";
import {
  AsyncSearchableSelect,
  type AsyncSelectOption,
} from "../../../components/Form/AsyncSearchableSelect";
import { loadAllOfficesForCompany } from "../../../api/loadAllOfficesForCompany";
import { getSafeReturnTo, withQueryParam } from "../../../utils/returnTo";
import { PageHeader } from "../../../components/PageHeader";
import PermissionGate from "../../../components/Permissions/PermissionGate";
import { usePermissions } from "../../../hooks/usePermissions";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import LoadingState from "../../../components/LoadingState";
import EmptyState from "../../../components/EmptyState";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal";
import { TranslationsList } from "../../../components/TranslationsList/TranslationsList";
import { ROUTES } from "../../../routes";
import { splitName } from "../../../utils/splitName";
import { formatDateForApi, formatDateForForm } from "../../../utils/dateFormatter";
import { ExternalReferencesPanel } from "../components/ExternalReferencesPanel";
import styles from "../AddressBook.module.css";

type ApiValidationError = {
  details?: Array<{ path: (string | number)[]; message: string }>;
};

function formatOfficeLabel(o: Office): string {
  const location = [o.city, o.countryName].filter(Boolean).join(", ");
  return location ? `${o.name} - ${location}` : o.name;
}

function formatCompanyLabel(c: Company): string {
  return c.firmRef ? `${c.name} (${c.firmRef})` : c.name;
}

const ROLE_TYPE_OPTIONS = [
  "Partner",
  "Associate",
  "Of Counsel",
  "Counsel",
  "Managing Partner",
  "Senior Associate",
  "Lawyer",
];

function buildRoleTypeOptions(storedRoleType?: string): string[] {
  if (storedRoleType && !ROLE_TYPE_OPTIONS.includes(storedRoleType)) {
    return [...ROLE_TYPE_OPTIONS, storedRoleType];
  }
  return ROLE_TYPE_OPTIONS;
}

type ContactFormData = {
  firstName: string;
  lastName: string;
  name: string;
  slug: string;
  email: string;
  jobTitle: string;
  translations: ContactTranslation[];
  companyId: string;
  officeId: string;
};

type RoleFormData = {
  companyId: string;
  officeId: string;
  roleType: string;
  jobTitleOverride: string;
  emailOverride: string;
  isPortalContact: boolean;
  startDate: string;
  endDate: string;
};

type CorrectFormData = {
  roleType: string;
  jobTitleOverride: string;
  emailOverride: string;
  startDate: string;
  endDate: string;
};

function ContactDetail() {
  const navigate = useNavigate();
  const {
    id,
    companyId: companyIdParam,
    officeId: officeIdParam,
  } = useParams<{
    id?: string;
    companyId?: string;
    officeId?: string;
  }>();
  const isNew = id === "new" || Boolean(companyIdParam) || Boolean(officeIdParam);
  const [searchParams] = useSearchParams();
  const prefilledName = splitName(isNew ? searchParams.get("name") : null);
  const prefilledFirstName = prefilledName.firstName;
  const prefilledLastName = prefilledName.lastName;
  // The prefill is written to the dirty baseline too, so the form is pristine on
  // arrival and Back does not warn. Submit still has to be reachable, so a prefilled
  // form counts as submittable without an edit.
  const hasPrefilledName = prefilledFirstName !== "" || prefilledLastName !== "";
  const { hasAnyPermission } = usePermissions();
  const { hasFlag } = useFeatureFlags();
  const isLive = hasFlag("address-book-live");
  const canWrite = hasAnyPermission(["addressbook.write"]) && isLive;

  // `?returnTo=` (create only) sends the operator back to where they came from — e.g. the
  // submissions Link contact modal — instead of the company/office page. Validated to
  // same-origin app paths, so a crafted link can't redirect off-site.
  const returnTo = isNew ? getSafeReturnTo(searchParams.get("returnTo")) : null;
  const exitPath =
    returnTo ??
    (companyIdParam
      ? ROUTES.addressBook.companyDetail(companyIdParam)
      : officeIdParam
        ? ROUTES.addressBook.officeDetail(officeIdParam)
        : ROUTES.addressBook.contacts);

  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState<Error | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [roleFormError, setRoleFormError] = useState<string | null>(null);

  const [extRefs, setExtRefs] = useState<ExternalReference[]>([]);
  const [officesList, setOfficesList] = useState<Office[]>([]);
  const [contactCompanyOption, setContactCompanyOption] = useState<AsyncSelectOption | null>(null);
  const [contactOfficeOption, setContactOfficeOption] = useState<AsyncSelectOption | null>(null);
  const [roleCompanyOption, setRoleCompanyOption] = useState<AsyncSelectOption | null>(null);
  const [roleOfficeOption, setRoleOfficeOption] = useState<AsyncSelectOption | null>(null);
  const [companyNameMap, setCompanyNameMap] = useState<Map<string, string>>(new Map());

  const [activeTab, setActiveTab] = useState<"details" | "roles" | "ext-refs">("details");
  const [deleteContactModalOpen, setDeleteContactModalOpen] = useState(false);
  const [deleteRoleId, setDeleteRoleId] = useState<string | null>(null);
  const [roles, setRoles] = useState<ContactRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [roleOfficesMap, setRoleOfficesMap] = useState<Map<string, Office>>(new Map());
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [roleModalOffices, setRoleModalOffices] = useState<Office[]>([]);
  const [roleFormErrors, setRoleFormErrors] = useState<Record<string, string>>({});
  const [isSavingRole, setIsSavingRole] = useState(false);
  const [isDeletingRole, setIsDeletingRole] = useState(false);
  const [discardChangesModalOpen, setDiscardChangesModalOpen] = useState(false);
  const [discardRoleModalOpen, setDiscardRoleModalOpen] = useState(false);
  const [hasMergeHistory, setHasMergeHistory] = useState(false);
  const [roleForm, setRoleForm] = useState<RoleFormData>({
    companyId: "",
    officeId: "",
    roleType: "Partner",
    jobTitleOverride: "",
    emailOverride: "",
    isPortalContact: false,
    startDate: "",
    endDate: "",
  });

  const [includeFormerRoles, setIncludeFormerRoles] = useState(false);

  const [endRoleTarget, setEndRoleTarget] = useState<ContactRole | null>(null);
  const [isEndingRole, setIsEndingRole] = useState(false);

  const [endOpenRolesPrompt, setEndOpenRolesPrompt] = useState<{
    newRoleStartDate: string;
    openRoles: ContactRole[];
  } | null>(null);
  const [rolesSelectedToEnd, setRolesSelectedToEnd] = useState<string[]>([]);
  const [isEndingSelectedRoles, setIsEndingSelectedRoles] = useState(false);

  const [correctModalOpen, setCorrectModalOpen] = useState(false);
  const [correctingRole, setCorrectingRole] = useState<ContactRole | null>(null);
  const [correctForm, setCorrectForm] = useState<CorrectFormData>({
    roleType: "Partner",
    jobTitleOverride: "",
    emailOverride: "",
    startDate: "",
    endDate: "",
  });
  const [isCorrectingSubmitting, setIsCorrectingSubmitting] = useState(false);
  const [correctFormError, setCorrectFormError] = useState<string | null>(null);
  const [correctEndDateError, setCorrectEndDateError] = useState<string | null>(null);

  const [formData, setFormData] = useState<ContactFormData>({
    firstName: "",
    lastName: "",
    name: "",
    slug: "",
    email: "",
    jobTitle: "",
    translations: [],
    companyId: "",
    officeId: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [translationsValid, setTranslationsValid] = useState(true);

  const initialFormDataRef = useRef<ContactFormData | null>(null);
  const initialRoleFormRef = useRef<RoleFormData | null>(null);
  const prevCompanyIdRef = useRef<string | null>(null);

  const isDirty =
    initialFormDataRef.current !== null &&
    JSON.stringify(formData) !== JSON.stringify(initialFormDataRef.current);

  const isRoleDirty =
    initialRoleFormRef.current !== null &&
    JSON.stringify(roleForm) !== JSON.stringify(initialRoleFormRef.current);

  const derivedName = [formData.firstName.trim(), formData.lastName.trim()]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    if (isNew) {
      const initial: ContactFormData = {
        firstName: prefilledFirstName,
        lastName: prefilledLastName,
        name: "",
        slug: "",
        email: "",
        jobTitle: "",
        translations: [],
        companyId: companyIdParam ?? "",
        officeId: "",
      };
      initialFormDataRef.current = initial;
      setFormData(initial);
      if (companyIdParam) {
        void hydrateCompanyOption(companyIdParam);
      } else if (officeIdParam) {
        void prefillFromOffice(officeIdParam);
      }
    }
    if (!isNew && id) {
      loadContact(id);
      loadRoles(id);
      contacts
        .getMergeHistory(id)
        .then(res => setHasMergeHistory(res.merges.length > 0))
        .catch(() => {
          /* merge history unavailable — hide button */
        });
    }
  }, [id, isNew, companyIdParam, officeIdParam]);

  useEffect(() => {
    if (formData.companyId) {
      loadOfficesForCompany(formData.companyId);
    } else if (prevCompanyIdRef.current) {
      setOfficesList([]);
      setFormData(prev => ({ ...prev, officeId: "" }));
      setContactOfficeOption(null);
    }
    prevCompanyIdRef.current = formData.companyId;
  }, [formData.companyId]);

  useEffect(() => {
    if (roleForm.companyId) {
      loadRoleModalOffices(roleForm.companyId);
    } else {
      setRoleModalOffices([]);
      setRoleForm(prev => ({ ...prev, officeId: "" }));
      setRoleOfficeOption(null);
    }
  }, [roleForm.companyId]);

  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (isDirty) {
        e.preventDefault();
      }
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  async function loadOfficesForCompany(companyId: string) {
    try {
      const allOffices = await loadAllOfficesForCompany(companyId);
      setOfficesList(allOffices);
      const currentOfficeId = formData.officeId;
      const matchingOffice = allOffices.find(o => o.id === currentOfficeId);
      if (matchingOffice) {
        setContactOfficeOption({
          value: matchingOffice.id,
          label: formatOfficeLabel(matchingOffice),
        });
      } else {
        setContactOfficeOption(null);
        if (currentOfficeId) {
          setFormData(prev => ({ ...prev, officeId: "" }));
        }
      }
    } catch (err) {
      console.error("Failed to load offices:", err);
      setToast({ message: "Failed to load offices list", type: "warning" });
    }
  }

  async function hydrateCompanyOption(companyId: string) {
    try {
      const res = await companies.getById(companyId);
      const c = res.data;
      setContactCompanyOption({
        value: c.id,
        label: `${c.name}${c.firmRef ? ` (${c.firmRef})` : ""}`,
      });
    } catch (err) {
      console.error("Failed to load company:", err);
      setToast({ message: "Could not load company details.", type: "warning" });
    }
  }

  async function prefillFromOffice(officeId: string) {
    try {
      const res = await offices.getById(officeId);
      const office = res.data;
      const prefilled: ContactFormData = {
        firstName: prefilledFirstName,
        lastName: prefilledLastName,
        name: "",
        slug: "",
        email: "",
        jobTitle: "",
        translations: [],
        companyId: office.companyId ?? "",
        officeId: office.id,
      };
      initialFormDataRef.current = prefilled;
      setFormData(prev => ({
        ...prev,
        companyId: prefilled.companyId,
        officeId: prefilled.officeId,
      }));
      setContactOfficeOption({ value: office.id, label: formatOfficeLabel(office) });
      if (office.companyId && office.companyName) {
        setContactCompanyOption({ value: office.companyId, label: office.companyName });
      } else if (office.companyId) {
        void hydrateCompanyOption(office.companyId);
      }
    } catch (err) {
      console.error("Failed to load office:", err);
      setToast({ message: "Could not load office details.", type: "warning" });
    }
  }

  async function loadRoleModalOffices(companyId: string) {
    try {
      const allOffices = await loadAllOfficesForCompany(companyId);
      setRoleModalOffices(allOffices);
    } catch (err) {
      console.error("Failed to load offices for role modal:", err);
      setToast({ message: "Failed to load offices list", type: "warning" });
    }
  }

  async function loadContact(contactId: string) {
    try {
      setLoading(true);
      setError(null);
      const response = await contacts.getById(contactId);
      setContact(response.data);
      const loadedFirst = response.data.firstName || "";
      const loadedLast = response.data.lastName || "";
      const loadedDerivedName = [loadedFirst.trim(), loadedLast.trim()].filter(Boolean).join(" ");
      const loaded: ContactFormData = {
        firstName: loadedFirst,
        lastName: loadedLast,
        name: response.data.name === loadedDerivedName ? "" : response.data.name || "",
        slug: response.data.slug || "",
        email: response.data.email || "",
        jobTitle: response.data.jobTitle || "",
        translations: response.data.translations ?? [],
        // Company/office pickers are create-only (SOFTSUPP-527); membership is managed via roles.
        companyId: "",
        officeId: "",
      };
      setFormData(loaded);
      initialFormDataRef.current = loaded;

      if (response.data.identityId) {
        try {
          const refsResponse = await externalReferences.getByEntity(
            "contact",
            response.data.identityId,
          );
          setExtRefs(refsResponse.data);
        } catch (err) {
          console.error("Failed to load external references for contact:", err);
          setExtRefs([]);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load contact"));
    } finally {
      setLoading(false);
    }
  }

  async function loadRoles(contactId: string, opts: { activeOnly?: boolean } = {}) {
    try {
      setRolesLoading(true);
      setRolesError(null);
      const activeOnly = opts.activeOnly !== undefined ? opts.activeOnly : !includeFormerRoles;
      const response = await contactRoles.getAll({ contactId, activeOnly });
      setRoles(response.data);

      const officeIds = [...new Set(response.data.map(r => r.officeId).filter(Boolean))];
      const officeResults = await Promise.allSettled(officeIds.map(oid => offices.getById(oid)));
      const officeMap = new Map<string, Office>();
      officeIds.forEach((oid, i) => {
        const result = officeResults[i];
        if (result.status === "fulfilled") {
          officeMap.set(oid, result.value.data);
        }
      });
      setRoleOfficesMap(officeMap);

      const companyIds = [
        ...new Set(
          Array.from(officeMap.values())
            .map(o => (o.companyId ? String(o.companyId) : null))
            .filter((c): c is string => Boolean(c)),
        ),
      ];
      if (companyIds.length > 0) {
        const companyResults = await Promise.allSettled(
          companyIds.map(cid => companies.getById(cid)),
        );
        const nameMap = new Map<string, string>();
        companyIds.forEach((cid, i) => {
          const result = companyResults[i];
          if (result.status === "fulfilled") {
            nameMap.set(cid, result.value.data.name);
          }
        });
        setCompanyNameMap(nameMap);
      } else {
        setCompanyNameMap(new Map());
      }
    } catch (err) {
      console.error("Failed to load roles:", err);
      setRolesError("Failed to load roles");
    } finally {
      setRolesLoading(false);
    }
  }

  function handleInputChange(field: keyof ContactFormData, value: string) {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }

  function validateForm(): boolean {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    }
    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }
    if (isNew && !formData.officeId) {
      errors.officeId = "Location is required";
    }
    const emailVal = formData.email?.trim();
    if (emailVal && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
      errors.email = "Invalid email format";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setFormError(null);
    try {
      if (isNew) {
        const validTranslations = formData.translations.filter(
          t => t.localeCode.trim() !== "" && t.translatedName.trim() !== "",
        );
        // The server derives a unique slug from the name when none is sent, so
        // only an explicitly typed slug is forwarded.
        const typedSlug = formData.slug.trim();
        const created = await contacts.create({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          name: formData.name?.trim() || derivedName || undefined,
          ...(typedSlug ? { slug: typedSlug } : {}),
          email: formData.email?.trim() || null,
          jobTitle: formData.jobTitle?.trim() || null,
          translations: validTranslations.length > 0 ? validTranslations : undefined,
          companyId: formData.companyId || undefined,
          officeId: formData.officeId || undefined,
        });
        initialFormDataRef.current = formData;
        setToast({ message: "Contact created successfully", type: "success" });
        // With a returnTo, hand the new contact's uuid back so the caller can preselect it.
        navigate(
          returnTo ? withQueryParam(returnTo, "createdContactId", created.data.id) : exitPath,
        );
      } else if (id) {
        const originalTranslations = initialFormDataRef.current?.translations ?? [];
        const originalLocaleCodes = new Set(originalTranslations.map(t => t.localeCode));
        const currentLocaleCodes = new Set(
          formData.translations.map(t => t.localeCode).filter(Boolean),
        );

        const removeTranslations = originalTranslations
          .map(t => t.localeCode)
          .filter(lc => !currentLocaleCodes.has(lc));

        const changedTranslations = formData.translations.filter(t => {
          if (!t.localeCode.trim() || !t.translatedName.trim()) return false;
          const original = originalTranslations.find(o => o.localeCode === t.localeCode);
          return !original || original.translatedName !== t.translatedName;
        });

        const newTranslations = formData.translations.filter(
          t =>
            t.localeCode.trim() &&
            t.translatedName.trim() &&
            !originalLocaleCodes.has(t.localeCode),
        );

        const translationsPayload = [...changedTranslations, ...newTranslations].filter(
          (t, i, arr) => arr.findIndex(x => x.localeCode === t.localeCode) === i,
        );

        // The API cannot clear a slug: an empty or unchanged field means
        // "leave the stored slug alone", so the field is omitted entirely.
        const initialSlug = initialFormDataRef.current?.slug.trim() ?? "";
        const typedSlug = formData.slug.trim();

        const response = await contacts.update(id, {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          name: formData.name?.trim() || derivedName || null,
          ...(typedSlug && typedSlug !== initialSlug ? { slug: typedSlug } : {}),
          email: formData.email?.trim() || null,
          jobTitle: formData.jobTitle?.trim() || null,
          translations: translationsPayload.length > 0 ? translationsPayload : undefined,
          removeTranslations: removeTranslations.length > 0 ? removeTranslations : undefined,
          // companyId/officeId are not sent — office membership is managed via contact_roles (SOFTSUPP-527).
        });
        setContact(response.data);
        initialFormDataRef.current = formData;
        setToast({ message: "Contact updated successfully", type: "success" });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save contact";
      if (err && typeof err === "object" && "details" in err) {
        const details = (err as ApiValidationError).details;
        if (details && Array.isArray(details)) {
          const fieldErrors: Record<string, string> = {};
          const messages: string[] = [];
          for (const d of details) {
            const field = String(d.path?.[0] || "");
            if (field) fieldErrors[field] = d.message;
            messages.push(d.message);
          }
          setFormErrors(fieldErrors);
          setFormError(messages.length > 0 ? messages.join(". ") : message);
        } else {
          setFormError(message);
        }
      } else {
        setFormError(message);
      }
      setToast({ message, type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDeleteContact() {
    if (!id || isNew) return;
    setIsDeleting(true);
    try {
      await contacts.delete(id);
      setToast({ message: "Contact deleted successfully", type: "success" });
      navigate(ROUTES.addressBook.contacts);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete contact";
      setToast({ message, type: "error" });
    } finally {
      setIsDeleting(false);
      setDeleteContactModalOpen(false);
    }
  }

  function handleBack() {
    if (isDirty) {
      setDiscardChangesModalOpen(true);
    } else {
      navigate(exitPath);
    }
  }

  function openAddRoleModal() {
    setRoleFormErrors({});
    setRoleFormError(null);
    const initial: RoleFormData = {
      companyId: "",
      officeId: "",
      roleType: "Partner",
      jobTitleOverride: "",
      emailOverride: "",
      isPortalContact: false,
      startDate: "",
      endDate: "",
    };
    setRoleForm(initial);
    initialRoleFormRef.current = initial;
    setRoleModalOffices([]);
    setRoleCompanyOption(null);
    setRoleOfficeOption(null);
    setRoleModalOpen(true);
  }

  function openCorrectRoleModal(role: ContactRole) {
    setCorrectingRole(role);
    setCorrectForm({
      roleType: role.roleType ?? "Partner",
      jobTitleOverride: role.jobTitleOverride ?? "",
      emailOverride: role.emailOverride ?? "",
      startDate: role.startDate ? role.startDate.slice(0, 10) : "",
      endDate: formatDateForForm(role.endDate) ?? "",
    });
    setCorrectFormError(null);
    setCorrectEndDateError(null);
    setCorrectModalOpen(true);
  }

  async function handleEndRole() {
    if (!endRoleTarget || isEndingRole) return;
    setIsEndingRole(true);
    try {
      await contactRoles.update(endRoleTarget.id, { endDate: new Date().toISOString() });
      setToast({ message: "Role ended successfully", type: "success" });
      await loadRoles(id!);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to end role";
      setToast({ message, type: "error" });
    } finally {
      setEndRoleTarget(null);
      setIsEndingRole(false);
    }
  }

  async function handleEndSelectedRoles() {
    if (!endOpenRolesPrompt || isEndingSelectedRoles || rolesSelectedToEnd.length === 0) return;
    setIsEndingSelectedRoles(true);
    const succeeded: string[] = [];
    const failed: string[] = [];
    for (const role of endOpenRolesPrompt.openRoles) {
      if (!rolesSelectedToEnd.includes(role.id)) continue;
      const label = `${role.roleType || "Role"} — ${resolveOfficeNameForRole(role)}`;
      try {
        await contactRoles.update(role.id, { endDate: endOpenRolesPrompt.newRoleStartDate });
        succeeded.push(label);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to end role";
        failed.push(`${label}: ${message}`);
      }
    }
    if (failed.length === 0) {
      setToast({ message: "Selected roles ended successfully", type: "success" });
    } else if (succeeded.length > 0) {
      setToast({
        message: `Ended: ${succeeded.join(", ")}. Failed to end: ${failed.join("; ")}`,
        type: "warning",
      });
    } else {
      setToast({ message: `Failed to end: ${failed.join("; ")}`, type: "error" });
    }
    setEndOpenRolesPrompt(null);
    setIsEndingSelectedRoles(false);
    await loadRoles(id!);
  }

  async function handleCorrectSubmit() {
    if (!correctingRole || isCorrectingSubmitting) return;
    const isFormerRole = Boolean(correctingRole.endDate);
    if (!isFormerRole) {
      const email = correctForm.emailOverride.trim();
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setCorrectFormError("Invalid email format");
        return;
      }
    }
    if (
      correctForm.endDate &&
      correctForm.startDate &&
      correctForm.endDate < correctForm.startDate
    ) {
      setCorrectEndDateError("End date cannot be before start date");
      return;
    }
    // An untouched end-date input must not rewrite the stored timestamp, so omit it.
    const endDateUnchanged =
      correctForm.endDate === (formatDateForForm(correctingRole.endDate) ?? "");
    const endDatePayload = endDateUnchanged
      ? {}
      : { endDate: correctForm.endDate ? formatDateForApi(correctForm.endDate) : null };
    if (isFormerRole && endDateUnchanged) {
      setCorrectModalOpen(false);
      setCorrectingRole(null);
      return;
    }
    setIsCorrectingSubmitting(true);
    setCorrectFormError(null);
    setCorrectEndDateError(null);
    try {
      // A terminated role only accepts end-date corrections, so send endDate alone.
      await contactRoles.update(
        correctingRole.id,
        isFormerRole
          ? endDatePayload
          : {
              roleType: correctForm.roleType || null,
              jobTitleOverride: correctForm.jobTitleOverride.trim() || null,
              emailOverride: correctForm.emailOverride.trim() || null,
              startDate: correctForm.startDate
                ? new Date(correctForm.startDate).toISOString()
                : undefined,
              ...endDatePayload,
            },
      );
      setCorrectModalOpen(false);
      setCorrectingRole(null);
      await loadRoles(id!);
      setToast({ message: "Role updated successfully", type: "success" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update role";
      setCorrectFormError(message);
      setToast({ message, type: "error" });
    } finally {
      setIsCorrectingSubmitting(false);
    }
  }

  function handleCloseRoleModal() {
    if (isRoleDirty) {
      setDiscardRoleModalOpen(true);
    } else {
      setRoleModalOpen(false);
    }
  }

  async function handleSaveRole() {
    if (isSavingRole) return;

    const errors: Record<string, string> = {};
    if (!roleForm.companyId) {
      errors.companyId = "Company is required";
    }
    const email = roleForm.emailOverride?.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.emailOverride = "Invalid email format";
    }
    setRoleFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSavingRole(true);
    setRoleFormError(null);
    try {
      const created = await contactRoles.create({
        contactId: id!,
        officeId: roleForm.officeId,
        roleType: roleForm.roleType || null,
        jobTitleOverride: roleForm.jobTitleOverride?.trim() || null,
        emailOverride: roleForm.emailOverride?.trim() || null,
        isPortalContact: roleForm.isPortalContact,
        startDate: roleForm.startDate ? new Date(roleForm.startDate).toISOString() : undefined,
        endDate: roleForm.endDate ? new Date(roleForm.endDate).toISOString() : undefined,
      });
      setToast({ message: "Role added successfully", type: "success" });
      setRoleModalOpen(false);
      const newRoleEnded = Boolean(created.data.endDate || roleForm.endDate);
      const otherOpenRoles = roles.filter(r => !r.endDate && r.id !== created.data.id);
      if (!newRoleEnded && otherOpenRoles.length > 0) {
        setRolesSelectedToEnd([]);
        setEndOpenRolesPrompt({
          newRoleStartDate: created.data.startDate ?? new Date().toISOString(),
          openRoles: otherOpenRoles,
        });
      }
      loadRoles(id!);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save role";
      if (err && typeof err === "object" && "details" in err) {
        const details = (err as ApiValidationError).details;
        if (details && Array.isArray(details)) {
          const fieldErrors: Record<string, string> = {};
          const messages: string[] = [];
          for (const d of details) {
            const field = String(d.path?.[0] || "");
            if (field) fieldErrors[field] = d.message;
            messages.push(d.message);
          }
          setRoleFormErrors(fieldErrors);
          setRoleFormError(messages.length > 0 ? messages.join(". ") : message);
        } else {
          setRoleFormError(message);
        }
      } else {
        setRoleFormError(message);
      }
      setToast({ message, type: "error" });
    } finally {
      setIsSavingRole(false);
    }
  }

  async function handleDeleteRole() {
    if (!deleteRoleId || isDeletingRole) return;
    setIsDeletingRole(true);
    try {
      await contactRoles.delete(deleteRoleId);
      setToast({ message: "Role deleted successfully", type: "success" });
      loadRoles(id!);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete role";
      setToast({ message, type: "error" });
    } finally {
      setDeleteRoleId(null);
      setIsDeletingRole(false);
    }
  }

  function findOfficeById(officeId: string): Office | undefined {
    const fromMap = roleOfficesMap.get(officeId);
    if (fromMap) return fromMap;
    for (const office of officesList) {
      if (office.id === officeId) return office;
    }
    for (const office of roleModalOffices) {
      if (office.id === officeId) return office;
    }
    return undefined;
  }

  function resolveCompanyNameForRole(role: ContactRole): string {
    const office = findOfficeById(role.officeId);
    if (!office?.companyId) return "—";
    return companyNameMap.get(String(office.companyId)) || "—";
  }

  function resolveOfficeNameForRole(role: ContactRole): string {
    const office = findOfficeById(role.officeId);
    return office?.name || "—";
  }

  if (error) {
    const status = (error as Error & { status?: number }).status;
    const isAuthError = status === 401 || status === 403;
    if (isAuthError) {
      return <UnauthorizedView />;
    }
  }

  if (loading) {
    return (
      <div className={styles.detailWrapper}>
        <PageHeader
          title={isNew ? "Create Contact" : "Contact Details"}
          subtitle={
            isNew ? "Add a new contact to the address book" : "View and edit contact information"
          }
        />
        <LoadingState message="Loading contact..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.detailWrapper}>
        <PageHeader title="Contact Details" subtitle="View and edit contact information" />
        <EmptyState
          variant="error"
          title="Failed to load contact"
          message={error.message}
          action={{
            label: "Back to Contacts",
            onClick: handleBack,
          }}
        />
      </div>
    );
  }

  const contactName = contact?.name || "Contact Details";
  const isEditingFormerRole = Boolean(correctingRole?.endDate);
  const canonicalCrm = extRefs.find(
    r => r.referenceType === "crm_entity_number" && r.isCanonical,
  )?.referenceValue;
  const canonicalFirmRef = extRefs.find(
    r => r.referenceType === "firm_ref" && r.isCanonical,
  )?.referenceValue;

  return (
    <div className={styles.detailWrapper}>
      <div>
        <PageHeader
          title={isNew ? "Create Contact" : contactName}
          subtitle={
            isNew ? "Add a new contact to the address book" : "View and edit contact information"
          }
          meta={
            !isNew && contact ? (
              <div className={styles.metaStrip}>
                <span className={styles.metaItem}>
                  <span className={styles.metaLabel}>WP Term ID</span>
                  <span className={styles.metaValue}>{contact.contactId}</span>
                </span>
                {canonicalCrm && (
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>CRM</span>
                    <span className={styles.metaValue}>{canonicalCrm}</span>
                  </span>
                )}
                {canonicalFirmRef && (
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>Firm Ref</span>
                    <span className={styles.metaValue}>{canonicalFirmRef}</span>
                  </span>
                )}
              </div>
            ) : undefined
          }
        >
          <div style={{ display: "flex", gap: "12px" }}>
            <Button variant="outlined" color="default" onClick={handleBack}>
              <Icon icon="arrow-left" size="small" />
              Back
            </Button>
            {!isNew && activeTab === "roles" && isLive && (
              <PermissionGate permission="addressbook.write">
                <Button variant="contained" color="primary" onClick={openAddRoleModal}>
                  Add Role
                </Button>
              </PermissionGate>
            )}
            {!isNew && isLive && (
              <PermissionGate permission="addressbook.write">
                <Button
                  variant="outlined"
                  color="default"
                  onClick={() => navigate(ROUTES.addressBook.contactMerge(id!))}
                >
                  Merge
                </Button>
              </PermissionGate>
            )}
            {!isNew && isLive && (
              <PermissionGate permission="addressbook.write">
                <Button
                  variant="outlined"
                  color="default"
                  disabled={roles.filter(r => !r.endDate).length === 0}
                  title={
                    roles.filter(r => !r.endDate).length === 0
                      ? "No active roles to split."
                      : undefined
                  }
                  onClick={() => navigate(ROUTES.addressBook.contactSplit(id!))}
                >
                  Split
                </Button>
              </PermissionGate>
            )}
            {!isNew && isLive && (
              <PermissionGate permission="addressbook.write">
                <Button
                  variant="outlined"
                  color="default"
                  disabled={!hasMergeHistory}
                  title={
                    hasMergeHistory
                      ? undefined
                      : "No merge history — nothing to undo. Use Split to separate roles into a new contact."
                  }
                  onClick={() => navigate(ROUTES.addressBook.contactDemerge(id!))}
                >
                  Undo Merge
                </Button>
              </PermissionGate>
            )}
            {!isNew && isLive && (
              <PermissionGate permission="addressbook.delete">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDeleteContactModalOpen(true)}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </Button>
              </PermissionGate>
            )}
          </div>
        </PageHeader>
      </div>

      {!isNew && (
        <div className={styles.tabBar}>
          <button
            className={activeTab === "details" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("details")}
          >
            Details
          </button>
          <button
            className={activeTab === "roles" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("roles")}
          >
            Roles {roles.length > 0 && <span className={styles.tabBadge}>{roles.length}</span>}
          </button>
          <button
            className={activeTab === "ext-refs" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("ext-refs")}
          >
            Ext. Refs
          </button>
        </div>
      )}

      {(isNew || activeTab === "details") && (
        <form onSubmit={handleSubmit} className={styles.formContainer}>
          {formError && (
            <div className={styles.formAlert} role="alert">
              {formError}
            </div>
          )}

          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="firstName">
                First Name *
              </label>
              <input
                id="firstName"
                type="text"
                className={`${styles.formInput} ${formErrors.firstName ? styles.formInputError : ""}`}
                value={formData.firstName}
                onChange={e => handleInputChange("firstName", e.target.value)}
                placeholder="Enter first name"
                disabled={!canWrite}
                aria-required="true"
                aria-invalid={!!formErrors.firstName}
                aria-describedby={formErrors.firstName ? "firstName-error" : undefined}
              />
              {formErrors.firstName && (
                <span id="firstName-error" className={styles.errorText}>
                  {formErrors.firstName}
                </span>
              )}
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="lastName">
                Last Name *
              </label>
              <input
                id="lastName"
                type="text"
                className={`${styles.formInput} ${formErrors.lastName ? styles.formInputError : ""}`}
                value={formData.lastName}
                onChange={e => handleInputChange("lastName", e.target.value)}
                placeholder="Enter last name"
                disabled={!canWrite}
                aria-required="true"
                aria-invalid={!!formErrors.lastName}
                aria-describedby={formErrors.lastName ? "lastName-error" : undefined}
              />
              {formErrors.lastName && (
                <span id="lastName-error" className={styles.errorText}>
                  {formErrors.lastName}
                </span>
              )}
            </div>

            <div className={`${styles.formField} ${styles.formFieldFull}`}>
              <label className={styles.formLabel} htmlFor="name">
                Name
              </label>
              <input
                id="name"
                type="text"
                className={styles.formInput}
                value={derivedName || formData.name}
                placeholder="Will be derived from first/last name"
                disabled
                aria-describedby="name-helper"
              />
              <span id="name-helper" className={styles.helperText}>
                Derived from first/last name
              </span>
            </div>

            <div className={`${styles.formField} ${styles.formFieldFull}`}>
              <label className={styles.formLabel} htmlFor="slug">
                Slug
              </label>
              <input
                id="slug"
                type="text"
                className={styles.formInput}
                value={formData.slug}
                onChange={e => handleInputChange("slug", e.target.value)}
                placeholder={isNew ? "auto-generated-from-name" : ""}
                disabled={!canWrite}
                aria-describedby={isNew && !formData.slug ? "slug-helper" : undefined}
              />
              {isNew && !formData.slug ? (
                <span id="slug-helper" className={styles.helperText}>
                  Leave blank to generate a unique slug from the name on save
                </span>
              ) : null}
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="text"
                className={`${styles.formInput} ${formErrors.email ? styles.formInputError : ""}`}
                value={formData.email || ""}
                onChange={e => handleInputChange("email", e.target.value)}
                placeholder="email@example.com"
                disabled={!canWrite}
                aria-invalid={!!formErrors.email}
                aria-describedby={formErrors.email ? "email-error" : undefined}
              />
              {formErrors.email && (
                <span id="email-error" className={styles.errorText}>
                  {formErrors.email}
                </span>
              )}
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="jobTitle">
                Job Title
              </label>
              <input
                id="jobTitle"
                type="text"
                className={styles.formInput}
                value={formData.jobTitle || ""}
                onChange={e => handleInputChange("jobTitle", e.target.value)}
                placeholder="Enter job title"
                disabled={!canWrite}
              />
            </div>

            {isNew && (
              <>
                <div className={`${styles.formField} ${styles.formFieldFull}`}>
                  <label className={styles.formLabel}>
                    Role: Firm and location this contact is connected with.
                  </label>
                </div>
                <AsyncSearchableSelect
                  name="companyId"
                  label="Firm"
                  value={formData.companyId || null}
                  onChange={value => handleInputChange("companyId", value ? String(value) : "")}
                  onSearch={async query => {
                    const res = await companies.getAll({
                      search: query || undefined,
                      limit: 25,
                    });
                    return res.data.map(c => ({
                      value: c.id,
                      label: formatCompanyLabel(c),
                    }));
                  }}
                  initialOption={contactCompanyOption}
                  minSearchLength={0}
                  debounceMs={300}
                  disabled={!canWrite}
                  placeholder="Select a company"
                  helperText='Roles can be managed through the "Roles" tab after a contact has been created.'
                />

                <AsyncSearchableSelect
                  name="officeId"
                  label="Location"
                  value={formData.officeId || null}
                  onChange={value => handleInputChange("officeId", value ? String(value) : "")}
                  onOptionChange={option => setContactOfficeOption(option)}
                  onSearch={async query => {
                    const q = query.trim().toLowerCase();
                    const filtered = q
                      ? officesList.filter(o => o.name?.toLowerCase().includes(q))
                      : officesList;
                    return filtered.map(o => ({ value: o.id, label: formatOfficeLabel(o) }));
                  }}
                  initialOption={contactOfficeOption}
                  minSearchLength={0}
                  debounceMs={0}
                  disabled={!formData.companyId || !canWrite}
                  placeholder={formData.companyId ? "Select an office" : "Select a company first"}
                  error={formErrors.officeId}
                  helperText={
                    formData.companyId && officesList.length === 0
                      ? "This company has no offices. Create an office first."
                      : undefined
                  }
                />
              </>
            )}
          </div>

          <div
            className={`${styles.formField} ${styles.formFieldFull}`}
            style={{ marginTop: "1.5rem" }}
          >
            <label className={styles.formLabel}>
              Translations
              <span className={styles.helperText} style={{ marginLeft: "0.5rem" }}>
                (optional) Localised names for this contact
              </span>
            </label>
            <TranslationsList
              translations={formData.translations}
              onChange={translations => setFormData(prev => ({ ...prev, translations }))}
              disabled={!canWrite}
              onValidityChange={setTranslationsValid}
            />
          </div>

          <div className={styles.formActions}>
            <Button variant="outlined" color="default" onClick={handleBack} disabled={isSubmitting}>
              {canWrite ? "Cancel" : "Back"}
            </Button>
            {isLive && (
              <PermissionGate permission="addressbook.write">
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isSubmitting || (!isDirty && !hasPrefilledName) || !translationsValid}
                >
                  {isSubmitting ? "Saving..." : isNew ? "Create Contact" : "Save Changes"}
                </Button>
              </PermissionGate>
            )}
          </div>
        </form>
      )}

      {!isNew && activeTab === "roles" && (
        <div>
          {isLive && (
            <div className={styles.rolesToolbar}>
              <label className={styles.checkboxField}>
                <Checkbox
                  checked={includeFormerRoles}
                  onChange={e => {
                    const next = e.target.checked;
                    setIncludeFormerRoles(next);
                    if (id) loadRoles(id, { activeOnly: !next });
                  }}
                />
                <span>Include former roles</span>
              </label>
            </div>
          )}

          {rolesLoading && <LoadingState message="Loading roles..." />}

          {!rolesLoading && rolesError && (
            <EmptyState
              variant="error"
              title="Failed to load roles"
              message={rolesError}
              action={{
                label: "Retry",
                onClick: () => loadRoles(id!),
              }}
            />
          )}

          {!rolesLoading && !rolesError && roles.length === 0 && (
            <div className={styles.emptyRoles}>
              <p>No roles assigned.</p>
              <p>Add a role to link this contact to an office.</p>
            </div>
          )}

          {!rolesLoading && !rolesError && roles.length > 0 && (
            <div className={styles.section}>
              <table className={styles.rolesTable}>
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Role Type</th>
                    <th>Job Title Override</th>
                    <th>Email Override</th>
                    <th>Office</th>
                    <th>Portal</th>
                    <th>Dates</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map(role => (
                    <tr key={role.id}>
                      <td>
                        {(() => {
                          const office = findOfficeById(role.officeId);
                          const companyId = office?.companyId;
                          const companyName = resolveCompanyNameForRole(role);
                          if (companyId && companyName !== "—") {
                            return (
                              <Link
                                to={ROUTES.addressBook.companyDetail(String(companyId))}
                                className={styles.companyLink}
                              >
                                {companyName}
                              </Link>
                            );
                          }
                          return companyName;
                        })()}
                      </td>
                      <td>
                        {role.roleType ? (
                          <span className={styles.roleBadge}>{role.roleType}</span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{role.jobTitleOverride || "—"}</td>
                      <td>{role.emailOverride || "—"}</td>
                      <td>{resolveOfficeNameForRole(role)}</td>
                      <td>
                        {role.isPortalContact ? (
                          <span className={styles.portalBadge}>Portal</span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {role.endDate && <span className={styles.formerBadge}>Former</span>}
                        {role.startDate || role.endDate ? (
                          <span>
                            {role.startDate ? new Date(role.startDate).toLocaleDateString() : "—"}
                            {" – "}
                            {role.endDate ? new Date(role.endDate).toLocaleDateString() : "Present"}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {isLive && (
                          <PermissionGate permission="addressbook.write">
                            <div style={{ display: "flex", gap: "8px" }}>
                              {!role.endDate && (
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  onClick={() => setEndRoleTarget(role)}
                                >
                                  End or Deactivate
                                </Button>
                              )}
                              <Button
                                variant="outlined"
                                color="default"
                                onClick={() => openCorrectRoleModal(role)}
                              >
                                Edit
                              </Button>
                              {!role.endDate && (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  onClick={() => setDeleteRoleId(role.id)}
                                >
                                  Delete
                                </Button>
                              )}
                            </div>
                          </PermissionGate>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!isNew && activeTab === "ext-refs" && contact && (
        <ExternalReferencesPanel
          entityType="contact"
          entityId={contact.identityId}
          canWrite={canWrite}
          addableRefTypes={["address_book", "crm_entity_number", "wordpress_term_id"]}
        />
      )}

      <Modal isOpen={roleModalOpen} onClose={handleCloseRoleModal} title="Add Role" blurBackground>
        <div className={styles.modalForm}>
          {roleFormError && (
            <div className={styles.formAlert} role="alert">
              {roleFormError}
            </div>
          )}

          <div className={styles.formGrid}>
            <AsyncSearchableSelect
              name="roleCompanyId"
              label="Company"
              required
              value={roleForm.companyId || null}
              onChange={value => {
                const next = value ? String(value) : "";
                setRoleForm(prev => ({ ...prev, companyId: next, officeId: "" }));
                if (roleFormErrors.companyId) {
                  setRoleFormErrors(prev => {
                    const n = { ...prev };
                    delete n.companyId;
                    return n;
                  });
                }
              }}
              onOptionChange={option => setRoleCompanyOption(option)}
              onSearch={async query => {
                const res = await companies.getAll({
                  search: query || undefined,
                  limit: 25,
                });
                return res.data.map(c => ({ value: c.id, label: formatCompanyLabel(c) }));
              }}
              initialOption={roleCompanyOption}
              minSearchLength={0}
              debounceMs={300}
              error={roleFormErrors.companyId}
              placeholder="Select a company"
            />

            <AsyncSearchableSelect
              name="roleOfficeId"
              label="Office"
              value={roleForm.officeId || null}
              onChange={value => {
                setRoleForm(prev => ({ ...prev, officeId: value ? String(value) : "" }));
                if (roleFormErrors.officeId) {
                  setRoleFormErrors(prev => {
                    const n = { ...prev };
                    delete n.officeId;
                    return n;
                  });
                }
              }}
              onOptionChange={option => setRoleOfficeOption(option)}
              onSearch={async query => {
                const q = query.trim().toLowerCase();
                const filtered = q
                  ? roleModalOffices.filter(o => o.name?.toLowerCase().includes(q))
                  : roleModalOffices;
                return filtered
                  .map(o => ({ value: o.id, label: formatOfficeLabel(o) }))
                  .sort((a, b) => a.label.localeCompare(b.label));
              }}
              initialOption={roleOfficeOption}
              minSearchLength={0}
              debounceMs={0}
              disabled={!roleForm.companyId}
              placeholder={roleForm.companyId ? "Select an office" : "Select a company first"}
              error={roleFormErrors.officeId}
            />
          </div>

          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="roleType">
                Role Type *
              </label>
              <select
                id="roleType"
                className={styles.formInput}
                value={roleForm.roleType}
                onChange={e => setRoleForm(prev => ({ ...prev, roleType: e.target.value }))}
              >
                {ROLE_TYPE_OPTIONS.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="roleJobTitleOverride">
                Job Title Override
              </label>
              <input
                id="roleJobTitleOverride"
                type="text"
                className={styles.formInput}
                value={roleForm.jobTitleOverride}
                onChange={e => setRoleForm(prev => ({ ...prev, jobTitleOverride: e.target.value }))}
                placeholder="Optional override"
              />
            </div>
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="roleEmailOverride">
              Email Override
            </label>
            <input
              id="roleEmailOverride"
              type="text"
              className={`${styles.formInput} ${roleFormErrors.emailOverride ? styles.formInputError : ""}`}
              value={roleForm.emailOverride}
              onChange={e => {
                setRoleForm(prev => ({ ...prev, emailOverride: e.target.value }));
                if (roleFormErrors.emailOverride) {
                  setRoleFormErrors(prev => {
                    const n = { ...prev };
                    delete n.emailOverride;
                    return n;
                  });
                }
              }}
              placeholder="Optional override"
              aria-invalid={!!roleFormErrors.emailOverride}
              aria-describedby={
                roleFormErrors.emailOverride ? "roleEmailOverride-error" : undefined
              }
            />
            {roleFormErrors.emailOverride && (
              <span id="roleEmailOverride-error" className={styles.errorText}>
                {roleFormErrors.emailOverride}
              </span>
            )}
          </div>

          <div className={styles.formField}>
            <label className={`${styles.formLabel} ${styles.labelWithTooltip}`}>
              Flags
              <Tooltip
                title=""
                content="Portal contacts are visible to subscribing firms in the profile portal and can manage their firm's profile"
                position="top"
              >
                <span className={styles.tooltipIcon}>?</span>
              </Tooltip>
            </label>
            <div className={styles.checkboxField}>
              <Checkbox
                checked={roleForm.isPortalContact}
                onChange={e =>
                  setRoleForm(prev => ({ ...prev, isPortalContact: e.target.checked }))
                }
              />
              <span>Is Portal Contact</span>
            </div>
          </div>

          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="roleStartDate">
                Start date
              </label>
              <input
                id="roleStartDate"
                type="date"
                className={styles.formInput}
                value={roleForm.startDate}
                onChange={e => setRoleForm(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="roleEndDate">
                End date
              </label>
              <input
                id="roleEndDate"
                type="date"
                className={styles.formInput}
                value={roleForm.endDate}
                onChange={e => setRoleForm(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          </div>

          <div className={styles.modalActions}>
            <Button variant="outlined" color="default" onClick={handleCloseRoleModal}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveRole}
              disabled={isSavingRole}
            >
              {isSavingRole ? "Saving..." : "Add"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={correctModalOpen}
        onClose={() => setCorrectModalOpen(false)}
        title="Edit role"
        blurBackground
      >
        <div className={styles.modalForm}>
          {correctFormError && (
            <div className={styles.formAlert} role="alert">
              {correctFormError}
            </div>
          )}

          {isEditingFormerRole && (
            <small className={styles.helperText}>
              Only the end date of an ended role can be corrected. Clear it to reactivate the role.
            </small>
          )}

          <div className={styles.formField}>
            <label className={styles.formLabel}>Office</label>
            <div className={styles.readOnly}>
              {correctingRole ? resolveOfficeNameForRole(correctingRole) : "—"}
            </div>
            <small className={styles.helperText}>
              To change office, end this role and add a new role at the target office.
            </small>
          </div>

          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="correctRoleType">
                Role type *
              </label>
              <select
                id="correctRoleType"
                className={styles.formInput}
                value={correctForm.roleType}
                onChange={e => setCorrectForm(prev => ({ ...prev, roleType: e.target.value }))}
                disabled={isEditingFormerRole}
              >
                {buildRoleTypeOptions(correctingRole?.roleType ?? undefined).map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="correctStartDate">
                Start date
              </label>
              <input
                id="correctStartDate"
                type="date"
                className={styles.formInput}
                value={correctForm.startDate}
                onChange={e => setCorrectForm(prev => ({ ...prev, startDate: e.target.value }))}
                disabled={isEditingFormerRole}
              />
            </div>
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="correctEndDate">
              End date
            </label>
            <input
              id="correctEndDate"
              type="date"
              className={`${styles.formInput} ${correctEndDateError ? styles.formInputError : ""}`}
              value={correctForm.endDate}
              onChange={e => {
                setCorrectForm(prev => ({ ...prev, endDate: e.target.value }));
                setCorrectEndDateError(null);
              }}
              aria-invalid={!!correctEndDateError}
              aria-describedby={correctEndDateError ? "correctEndDate-error" : undefined}
            />
            {correctEndDateError && (
              <span id="correctEndDate-error" className={styles.errorText}>
                {correctEndDateError}
              </span>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="correctJobTitle">
              Job title (override)
            </label>
            <input
              id="correctJobTitle"
              type="text"
              className={styles.formInput}
              value={correctForm.jobTitleOverride}
              onChange={e =>
                setCorrectForm(prev => ({ ...prev, jobTitleOverride: e.target.value }))
              }
              placeholder="Optional override"
              disabled={isEditingFormerRole}
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="correctEmail">
              Email (override)
            </label>
            <input
              id="correctEmail"
              type="text"
              className={styles.formInput}
              value={correctForm.emailOverride}
              onChange={e => setCorrectForm(prev => ({ ...prev, emailOverride: e.target.value }))}
              placeholder="Optional override"
              disabled={isEditingFormerRole}
            />
          </div>

          <div className={styles.modalActions}>
            <Button
              variant="outlined"
              color="default"
              onClick={() => setCorrectModalOpen(false)}
              disabled={isCorrectingSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCorrectSubmit}
              disabled={isCorrectingSubmitting}
            >
              {isCorrectingSubmitting ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={endOpenRolesPrompt !== null}
        onClose={() => setEndOpenRolesPrompt(null)}
        title="End other active roles?"
        blurBackground
      >
        <div className={styles.modalForm}>
          <p>
            This contact still has other active roles. Select any that should be ended as of the new
            role's start date.
          </p>
          {endOpenRolesPrompt?.openRoles.map(role => (
            <label key={role.id} className={styles.checkboxField}>
              <Checkbox
                checked={rolesSelectedToEnd.includes(role.id)}
                onChange={e => {
                  const checked = e.target.checked;
                  setRolesSelectedToEnd(prev =>
                    checked ? [...prev, role.id] : prev.filter(rid => rid !== role.id),
                  );
                }}
              />
              <span>{`${role.roleType || "Role"} — ${resolveOfficeNameForRole(role)}`}</span>
            </label>
          ))}
          <div className={styles.modalActions}>
            <Button
              variant="outlined"
              color="default"
              onClick={() => setEndOpenRolesPrompt(null)}
              disabled={isEndingSelectedRoles}
            >
              Skip
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEndSelectedRoles}
              disabled={isEndingSelectedRoles || rolesSelectedToEnd.length === 0}
            >
              {isEndingSelectedRoles ? "Ending..." : "End selected"}
            </Button>
          </div>
        </div>
      </Modal>

      {endRoleTarget && (
        <ConfirmationModal
          title="End Role"
          message="Are you sure you want to end this role?"
          warningMessage="The role will be marked as ended today and the contact will show as Former for this office."
          confirmButtonText="End Role"
          confirmButtonColor="error"
          size="small"
          onClose={() => setEndRoleTarget(null)}
          onConfirm={handleEndRole}
        />
      )}

      {deleteContactModalOpen && (
        <ConfirmationModal
          title="Delete Contact"
          message={`Are you sure you want to delete "${contact?.name}"?`}
          warningMessage={
            roles.filter(r => !r.endDate).length > 0
              ? "This will archive the contact and end all active roles. This action cannot be undone."
              : "This will archive the contact. This action cannot be undone."
          }
          affectedItems={roles
            .filter(r => !r.endDate)
            .map(r => ({
              id: r.id,
              name: `${r.roleType || "Role"} — ${resolveOfficeNameForRole(r)}`,
            }))}
          confirmButtonText="Delete"
          confirmButtonColor="error"
          size="small"
          onClose={() => setDeleteContactModalOpen(false)}
          onConfirm={handleDeleteContact}
        />
      )}

      {deleteRoleId && (
        <ConfirmationModal
          title="Delete Role"
          message="Are you sure you want to delete this role?"
          warningMessage="This will remove the contact's assignment to this office."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          size="small"
          onClose={() => setDeleteRoleId(null)}
          onConfirm={handleDeleteRole}
        />
      )}

      {discardChangesModalOpen && (
        <ConfirmationModal
          title="Discard Changes"
          message="You have unsaved changes. Are you sure you want to leave?"
          warningMessage="Any unsaved changes will be lost."
          confirmButtonText="Discard"
          confirmButtonColor="error"
          size="small"
          onClose={() => setDiscardChangesModalOpen(false)}
          onConfirm={async () => navigate(exitPath)}
        />
      )}

      {discardRoleModalOpen && (
        <ConfirmationModal
          title="Discard Changes"
          message="You have unsaved changes. Are you sure you want to close?"
          warningMessage="Any unsaved changes will be lost."
          confirmButtonText="Discard"
          confirmButtonColor="error"
          size="small"
          onClose={() => setDiscardRoleModalOpen(false)}
          onConfirm={async () => {
            setDiscardRoleModalOpen(false);
            setRoleModalOpen(false);
          }}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default ContactDetail;
